import { FC, useMemo, useState } from 'react';
import { Column } from 'react-table';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';
import { QueryClient, useQuery, useQueryClient } from 'react-query';
import {
  FaCheck,
  FaListAlt,
  FaFile,
  FaFileSignature,
  FaInfo,
  FaFilePrescription,
  FaTimes,
  FaTrash,
  FaEdit,
} from 'react-icons/fa';

import API from 'core/Api';
import { createErrorFromResponse } from 'core/Error';

import { useModal } from 'store/modal';
import { PermissionKeys } from 'store/types';
import { useLanguageContext } from 'store/language';
import { useActiveClientContext } from 'store/active-client';
import { usePermissionsContext } from 'store/permissions';

import { formatDate } from 'utils/helpers/formatters';
import { isIntigerOrFloat } from 'utils/helpers/leaves';
import { buildClientEntity, buildClientUrl } from 'utils/helpers/client';
import { multiSignDocAsEmployer, signDocAsEmployer } from 'routes/client/documents/helpers';
import Button from 'components/Button';
import FetchTable from 'components/table/FetchTable';
import BadgeComponent from 'components/Badge';
import TooltipWrapper from 'components/TooltipWrapper';
import { IАttachment } from 'components/Attachment';
import { ITableFilter } from 'components/table/types';
import { buildFilters } from 'components/table/utils/filtersConfig';
import { ISelectOptionProps } from 'components/form/types';
import handlePreviewDoc from 'utils/helpers/handlePreviewDoc';
import Spinner from 'components/Spinner';
import IconReview from 'components/table/filterIcons/IconReview';
import ReviewDoc from 'components/documents/ReviewDoc';
import { getUserInfo } from 'utils/auth';
import { ILeave } from '../types';
import RejectLeave from '../components/RejectLeave';
import EmployerRejectLeave from '../components/EmployerRejectLeave';
import LeaveInterruptionInfoButton from '../components/LeaveInterruptionInfoButton';
import InnerWrapper from '../InnerWrapper';

interface IConfirmButtonProps {
  employeeRejectionRequestId: number;
  documentHolderId: number;
}

interface IReviewFilterIconProps {
  ariaLabel: string;
  onToggle: (next: boolean) => void;
}

/**
 * Keeps its own `isSelected` state instead of receiving it via props, so that toggling
 * the review filter doesn't change the identity of the `tableFilters` array in `Screen`.
 * FetchTable resets all of its filters (including already selected dates) whenever that
 * array reference changes, so the array must stay referentially stable across toggles.
 */
const ReviewFilterIcon: FC<IReviewFilterIconProps> = ({ ariaLabel, onToggle }) => {
  const [isSelected, setIsSelected] = useState(false);

  return (
    <IconReview
      ariaLabel={ariaLabel}
      isSelected={isSelected}
      onClick={() => {
        setIsSelected(prev => {
          const next = !prev;
          onToggle(next);
          return next;
        });
      }}
    />
  );
};

type ButtonsType = {
  type: string;
  text: string;
  action_link: string;
  doc_holder_id?: number;
  disabled?: boolean;
  disabled_reason?: string;
};

const Screen: FC = () => {
  const { t } = useTranslation();
  const history = useHistory();

  const { client } = useActiveClientContext();
  const { language } = useLanguageContext();
  const { showModal, hideModal } = useModal();
  const queryClient = useQueryClient();

  const [index, setIndex] = useState<number | null>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isOrderPreviewing, setIsOrderPreviewing] = useState(false);
  const [isApproving, setIsAproving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isReviewActive, setIsReviewActive] = useState(false);
  const [customSelectableRowProps, setCustomSelectableRowProps] = useState<string[]>([]);
  const [multiActionActive, setMultiActionActive] = useState(false);

  const { featureLeaveReview } = getUserInfo();
  const { checkPermission } = usePermissionsContext();

  const createGroup = () => {
    const url = buildClientUrl('/leaves/completed/create', client);
    history.push(url);
  };

  const handleAttachmentClick = (id: number) => {
    return API.get(`leaves/attachment/${id}/download`)
      .then(res => {
        window.open(res.data, '_blank');
      })
      .catch(res => {
        const error = createErrorFromResponse(res);
        showModal({
          type: 'error',
          content: error.message,
        });
      });
  };

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const handleAttachmentsModal = (
    attachments: IАttachment[],
    leaveId: number,
    query: QueryClient,
    button?: ButtonsType,
    isSickLeave?: boolean
  ) => {
    const links = attachments.map(attachment => {
      return {
        id: attachment.id,
        fileName: `${attachment.file_name}.${attachment.extension}`,
      };
    });

    showModal({
      type: 'list',
      heading: t('leaves.attachmentsList'),
      onLinkClick: handleAttachmentClick,
      content:
        isSickLeave && !!button && featureLeaveReview ? (
          <ReviewDoc
            key={`${leaveId}-review`}
            id={leaveId.toString()}
            buttonText={
              button?.disabled
                ? button?.disabled_reason ?? t('documents.table.actionReview')
                : button?.text ?? t('documents.table.actionReview')
            }
            invalidate={() => query.invalidateQueries(buildClientEntity(`leaves-${isReviewActive}`, client))}
            disabled={!!button?.disabled}
          />
        ) : undefined,
      links,
    });
  };

  const approveLeave = (leaveId: number) => {
    return API.get(`leaves/${leaveId}/documents-holders`)
      .then(res => res.data)
      .then(({ request_document_holder_id: reqDocHolderId, order_document_holder_id: orderDocHolderId }) => {
        showModal({
          type: 'loading',
          heading: t('documents.eSign'),
          content: t('documents.eSignSubtitle'),
        });
        multiSignDocAsEmployer(
          [reqDocHolderId, orderDocHolderId],
          showModal,
          () => queryClient.resetQueries(buildClientEntity(`leaves-${isReviewActive}`, client)),
          '',
          t
        );
      })
      .catch(err =>
        showModal({
          type: 'error',
          content: createErrorFromResponse(err).message,
        })
      );
  };

  const handleDeleteModal = (deleteLink: string) => {
    showModal({
      type: 'confirm',
      heading: t('leaves.deleteHeading'),
      onLinkClick: handleAttachmentClick,
      content: t('leaves.deleteConfirmation'),
      onConfirm: () => {
        API.delete(deleteLink).then(() => {
          queryClient.invalidateQueries(buildClientEntity(`leaves-${isReviewActive}`, client));
        });
      },
    });
  };

  const formatUserAction = (
    actionArray: {
      user_name: string;
      timestamp: string;
    }[],
    actionType: string,
    rejectReason?: string
  ) => {
    return actionArray.map((action, indexa) => (
      <div key={`${actionType}-${action.user_name}-${action.timestamp}-${indexa}`}>
        {actionType} {t('leaves.from')} {action.user_name} {t('leaves.on')}{' '}
        {formatDate(new Date(action.timestamp), 'dd.mm.Y hh:ii')}
        {t('leaves.hoursSmall')}
        {rejectReason ? (
          <div>
            {t('leaves.rejectReason')}: {rejectReason}
          </div>
        ) : null}
      </div>
    ));
  };

  const getTooltipContent = (leave: ILeave) => {
    const shouldRenderDetails = leave.approved_by || leave.rejected_by || leave.signed_by;

    if (!shouldRenderDetails) {
      return (
        <div>
          {t('leaves.tooltip.id', { id: leave.id })}
          {leave?.substitute_employee_name ? (
            <div>
              {t('leaves.substituteEmployeeName')}: {leave.substitute_employee_name}
            </div>
          ) : null}
        </div>
      );
    }

    return (
      <div>
        <b>{t('leaves.tooltip.id', { id: leave.id })}</b>

        {leave?.substitute_employee_name ? (
          <div>
            {t('leaves.substituteEmployeeName')}: {leave.substitute_employee_name}
          </div>
        ) : null}

        {leave.approved_by?.length > 0 && formatUserAction(leave.approved_by, t('leaves.actions.approved'))}

        {leave.signed_by?.length > 0 && formatUserAction(leave.signed_by, t('leaves.actions.signed'))}

        {leave.rejected_by?.length > 0 &&
          formatUserAction(leave.rejected_by, t('leaves.actions.rejected'), leave.reject_reason)}
      </div>
    );
  };

  const handleCommentsModal = (comment: string) => {
    showModal({
      type: 'info',
      heading: t('leaves.comment'),
      onLinkClick: handleAttachmentClick,
      content: comment,
    });
  };
  const ConfirmEmployeeRejectRequestButton: FC<IConfirmButtonProps> = ({
    employeeRejectionRequestId,
    documentHolderId,
  }: IConfirmButtonProps) => {
    const [isLoadingConfirmReject, setIsLoadingConfirmReject] = useState(false);
    return (
      <Button
        type="button"
        btnType="info"
        loading={isLoadingConfirmReject}
        onClick={() => {
          if (isLoadingConfirmReject) {
            return;
          }

          setIsLoadingConfirmReject(true);

          API.post(`documents/${employeeRejectionRequestId}/approve`)
            .then(res => {
              signDocAsEmployer(
                employeeRejectionRequestId.toString(),
                documentHolderId.toString(),
                showModal,
                hideModal,
                () => queryClient.invalidateQueries(buildClientEntity(`leaves-${isReviewActive}`, client)),
                'documents.table.approvedAndSignedDocSuccessMsg',
                t
              );
            })
            .catch(err => {
              signDocAsEmployer(
                employeeRejectionRequestId.toString(),
                documentHolderId.toString(),
                showModal,
                hideModal,
                () => queryClient.invalidateQueries(buildClientEntity(`leaves-${isReviewActive}`, client)),
                'documents.table.approvedAndSignedDocSuccessMsg',
                t
              );
            });
        }}
      >
        {t('leaves.confirmEmployeeRejection')}
      </Button>
    );
  };

  const renderActions = (
    buttons: ButtonsType[],
    leaveId: number,
    requestDocId: number,
    employeeRejectionRequestId: number,
    orderDocId: number,
    rejectDocId: number,
    attachments: IАttachment[],
    status: string,
    commentText: string,
    isSickLeave: boolean | undefined,
    leaveDetails: ILeave
  ) => {
    const actions =
      buttons?.map((button, i) => {
        const { type, text, action_link: actionLink, doc_holder_id: documentHolderId } = button;
        if (type === 'approve')
          return (
            <div
              key={`approve-${leaveId}`}
              className="d-inline-block ml-2"
            >
              <TooltipWrapper
                id="tooltip-approve"
                overlay={t('leaves.approveAndSignOrder')}
              >
                <Button
                  type="button"
                  btnType="info"
                  size="sm"
                  disabled={isApproving && leaveId === index}
                  loading={isApproving && leaveId === index}
                  onClick={() => {
                    setIndex(leaveId);
                    setIsAproving(true);
                    approveLeave(leaveId).then(() => {
                      setIndex(null);
                      setIsAproving(false);
                    });
                  }}
                >
                  <FaCheck size={14} />
                </Button>
              </TooltipWrapper>
            </div>
          );
        if (type === 'reject')
          return (
            <RejectLeave
              key={`reject-${leaveId}`}
              leaveId={leaveId}
              buttonText={text}
              handlePreview={() => handlePreviewDoc({ showModal, id: requestDocId })}
              invalidate={() => queryClient.invalidateQueries(buildClientEntity(`leaves-${isReviewActive}`, client))}
            />
          );
        if (type === 'employeer_confirm_reject' && documentHolderId) {
          return (
            <div
              key={`reject-employer-${leaveId}`}
              className="d-inline-block ml-2"
            >
              <TooltipWrapper
                id="tooltip-approve"
                overlay={text}
              >
                <Button
                  type="button"
                  btnType="info"
                  onClick={() => {
                    showModal({
                      type: 'form',
                      heading: t('documents.table.confirmDocHeading'),
                      content: (
                        <>
                          <p>{t('leaves.employer.confirmLeaveRejectRequestMsg')}</p>
                          <hr />
                          <ConfirmEmployeeRejectRequestButton
                            employeeRejectionRequestId={employeeRejectionRequestId}
                            documentHolderId={documentHolderId}
                          />
                        </>
                      ),
                    });
                  }}
                  size="sm"
                >
                  <FaCheck size={14} />
                </Button>
              </TooltipWrapper>
            </div>
          );
        }
        if (type === 'employeer_deny_reject')
          return (
            <div
              className="d-inline-block ml-2"
              key={`reject-deny-${leaveId}`}
            >
              <TooltipWrapper
                id="tooltip-reject"
                overlay={text}
              >
                <Button
                  type="button"
                  btnType="danger"
                  size="sm"
                  loading={isLoading && leaveId === index}
                  onClick={() => {
                    setIndex(leaveId);
                    setIsLoading(true);
                    API.patch(actionLink)
                      .then(res => {
                        setIsLoading(false);
                        setIndex(null);
                        queryClient.resetQueries(buildClientEntity(`leaves-${isReviewActive}`, client));
                      })
                      .catch(err =>
                        showModal({
                          type: 'error',
                          content: createErrorFromResponse(err).message,
                        })
                      );
                  }}
                >
                  <FaTimes size={14} />
                </Button>
              </TooltipWrapper>
            </div>
          );
        if (type === 'employer_reject')
          return (
            <EmployerRejectLeave
              key={`reject-employer-${leaveId}`}
              leaveId={leaveId}
              buttonText={text}
              handlePreview={() => handlePreviewDoc({ showModal, id: orderDocId })}
              invalidate={() => queryClient.invalidateQueries(buildClientEntity(`leaves-${isReviewActive}`, client))}
            />
          );
        if (type === 'rejection_request_preview') {
          return (
            <div
              key={`reject-req-preview-${leaveId}`}
              className="d-inline-block ml-2"
            >
              <TooltipWrapper
                id="tooltip-approve"
                overlay={text}
              >
                <Button
                  type="button"
                  btnType="danger"
                  size="sm"
                  disabled={isPreviewing && leaveId === index}
                  onClick={() => {
                    setIndex(leaveId);
                    handlePreviewDoc({ showModal, id: employeeRejectionRequestId }).then(() => {
                      setIndex(null);
                    });
                  }}
                >
                  {!(isPreviewing && leaveId === index) && <FaFile size={14} />}

                  {isPreviewing && leaveId === index && <Spinner size={14} />}
                </Button>
              </TooltipWrapper>
            </div>
          );
        }
        if (type === 'order_preview') {
          return (
            <div
              key={`order-preview-${leaveId}`}
              className="d-inline-block ml-2"
            >
              <TooltipWrapper
                id="tooltip-approve"
                overlay={text}
              >
                <Button
                  type="button"
                  btnType="primary"
                  size="sm"
                  disabled={isOrderPreviewing && leaveId === index}
                  onClick={() => {
                    setIndex(leaveId);
                    setIsOrderPreviewing(true);
                    handlePreviewDoc({ showModal, id: orderDocId }).then(() => {
                      setIndex(null);
                      setIsOrderPreviewing(false);
                    });
                  }}
                >
                  {!(isOrderPreviewing && leaveId === index) && <FaFileSignature size={14} />}

                  {isOrderPreviewing && leaveId === index && <Spinner size={14} />}
                </Button>
              </TooltipWrapper>
            </div>
          );
        }
        if (type === 'reject_preview' && status === 'rejected') {
          return (
            <div
              className="d-inline-block ml-2"
              key={`reject-preview-${leaveId}`}
            >
              <TooltipWrapper
                id="tooltip-reject"
                overlay={text}
              >
                <Button
                  type="button"
                  btnType="primary"
                  size="sm"
                  disabled={isLoadingPreview && leaveId === index}
                  onClick={() => handlePreviewDoc({ showModal, id: rejectDocId, setIsLoading: setIsLoadingPreview })}
                >
                  {!isLoadingPreview && <FaFilePrescription size={14} />}

                  {isLoadingPreview && <Spinner size={14} />}
                </Button>
              </TooltipWrapper>
            </div>
          );
        }

        if (type === 'request_preview') {
          return (
            <div
              key={`req-preview-${leaveId}`}
              className="d-inline-block ml-2"
            >
              <TooltipWrapper
                id="tooltip-approve"
                overlay={text}
              >
                <Button
                  type="button"
                  btnType="info"
                  size="sm"
                  disabled={isPreviewing && leaveId === index}
                  onClick={() => {
                    setIndex(leaveId);
                    setIsPreviewing(true);
                    handlePreviewDoc({ showModal, id: requestDocId }).then(() => {
                      setIndex(null);
                      setIsPreviewing(false);
                    });
                  }}
                >
                  {!(isPreviewing && leaveId === index) && <FaFile size={14} />}

                  {isPreviewing && leaveId === index && <Spinner size={14} />}
                </Button>
              </TooltipWrapper>
            </div>
          );
        }
        if (type === 'attachments') {
          return (
            <div
              key={`attachments-${leaveId}`}
              className="d-inline-block ml-2"
            >
              <TooltipWrapper
                id="tooltip-attachments"
                overlay={t('leaves.attachmentsList')}
              >
                <Button
                  type="button"
                  btnType="info"
                  size="sm"
                  ariaLabel="Attachments List"
                  onClick={() =>
                    handleAttachmentsModal(
                      attachments,
                      leaveId,
                      queryClient,
                      buttons.find(btn => btn?.type === 'review'),
                      isSickLeave
                    )
                  }
                >
                  <FaListAlt size={14} />
                </Button>
              </TooltipWrapper>
            </div>
          );
        }
        if (type === 'comment') {
          return (
            <div
              key={`comment-${leaveId}`}
              className="d-inline-block ml-2"
            >
              <TooltipWrapper
                id="tooltip-comment"
                overlay={
                  <>
                    {text}: <br /> {commentText}
                  </>
                }
              >
                <Button
                  type="button"
                  btnType="info"
                  size="sm"
                  onClick={() => handleCommentsModal(commentText)}
                >
                  <FaInfo size={14} />
                </Button>
              </TooltipWrapper>
            </div>
          );
        }

        if (type === 'delete') {
          return (
            <div
              key={`delete-${leaveId}`}
              className="d-inline-block ml-2"
            >
              <TooltipWrapper
                id="tooltip-delete"
                overlay={<>{text}</>}
              >
                <Button
                  type="button"
                  btnType="warning"
                  size="sm"
                  onClick={() => handleDeleteModal(actionLink)}
                >
                  <FaTrash size={14} />
                </Button>
              </TooltipWrapper>
            </div>
          );
        }

        if (type === 'edit') {
          return (
            <div
              key={`edit-${leaveId}`}
              className="d-inline-block ml-2"
            >
              <TooltipWrapper
                id="tooltip-edit"
                overlay={<>{text}</>}
              >
                <Button
                  type="button"
                  btnType="primary"
                  outlined
                  size="sm"
                  onClick={() => history.push(`/leaves/manually/edit/${leaveId}`)}
                >
                  <FaEdit size={14} />
                </Button>
              </TooltipWrapper>
            </div>
          );
        }

        if (button.type === 'review') {
          return (
            <ReviewDoc
              key={`${leaveId}-review`}
              id={leaveId.toString()}
              buttonText={button?.disabled ? button?.disabled_reason ?? t('documents.table.actionReview') : button.text}
              invalidate={() => queryClient.invalidateQueries(buildClientEntity(`leaves-${isReviewActive}`, client))}
              disabled={!!button?.disabled}
            />
          );
        }

        return null;
      }) || [];

    const { interrupting_sick_leave: interruptingSickLeave, interrupted_leaves: interruptedLeaves } = leaveDetails;

    if (interruptingSickLeave || (interruptedLeaves && interruptedLeaves.length > 0)) {
      actions.unshift(
        <LeaveInterruptionInfoButton
          key={`${leaveId}-interruption-info`}
          leaveId={leaveId}
          leaveDetails={leaveDetails}
        />
      );
    }

    return <div className="d-flex justify-content-end">{actions}</div>;
  };

  const renderBadge = (status: string) => {
    const variant: {
      [key: string]: 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'info' | 'light' | 'dark';
    } = {
      waiting_action: 'primary',
      in_progress: 'warning',
      rejected: 'danger',
      employeeRejectInitiated: 'warning',
    };

    const statuses: { [key: string]: string } = {
      initiated: t('leaves.initiated'),
      pending: t('leaves.pending'),
      signing: t('leaves.signing'),
      confirmed: t('leaves.confirmed'),
      approved: t('leaves.approved'),
      rejected: t('leaves.rejected'),
      cancelled: t('leaves.cancelled'),
      employeeRejectInitiated: t('leaves.employeeRejectInitiated'),
      interrupted: t('leaves.interrupted'),
    };

    if (status === 'rejected') {
      return (
        <BadgeComponent
          variant={variant[status]}
          label={statuses[status]}
          normalBadgeStyles
        />
      );
    }

    if (status === 'employeeRejectInitiated') {
      return (
        <BadgeComponent
          variant={variant[status]}
          label={statuses[status]}
          normalBadgeStyles
        />
      );
    }

    return <span style={{ fontSize: '14px', fontWeight: 'normal' }}>{statuses[status]}</span>;
  };

  const columns: Array<Column<ILeave>> = [
    {
      Header: 'ID',
      accessor: 'id',
      disableSortBy: true,
    },
    {
      Header: t('leaves.employer.name') || '',
      accessor: 'user_name',
      width: '15%',
      disableSortBy: true,
      Cell: ({ row }) => {
        return (
          <TooltipWrapper
            id={`tooltip-name-${row.original.id}`}
            overlay={getTooltipContent(row.original)}
          >
            <span>{row.original.user_name}</span>
          </TooltipWrapper>
        );
      },
    },
    {
      Header: t('documents.table.group') || '',
      accessor: 'group_names',
      disableSortBy: true,
      width: '15%',
      Cell: ({ row }) => {
        const fullGroups = row.original.group_names.join(', ');
        const groupsPreview = fullGroups.length > 20 ? `${fullGroups.substr(0, 20)}...` : fullGroups;

        return (
          <TooltipWrapper
            id={`tooltip-group-${row.original.id}`}
            overlay={getTooltipContent(row.original)}
          >
            <span>{groupsPreview}</span>
          </TooltipWrapper>
        );
      },
    },
    {
      Header: t('leaves.type') || '',
      accessor: 'type_label',
      disableSortBy: true,
      width: '22%',
      Cell: ({ row }) => {
        return (
          <TooltipWrapper
            id={`tooltip-type-${row.original.id}`}
            overlay={getTooltipContent(row.original)}
          >
            <span>{row.original.type_label || ''}</span>
          </TooltipWrapper>
        );
      },
    },
    {
      Header: t('leaves.dateFrom') || '',
      accessor: 'date_from',
      width: '7.5rem',
      Cell: ({ cell }) => {
        const dateFrom = new Date(cell.row.original.date_from);
        const subtractedDaysByYears = cell.row.original.subtracted_days_by_years;

        // Filter out entries where days are 0
        const filteredEntries = subtractedDaysByYears
          ? Object.entries(subtractedDaysByYears).filter(([_, days]) => days !== 0)
          : [];

        const tooltipContent =
          filteredEntries.length > 0 ? (
            <div>
              {filteredEntries.map(([year, days]) => (
                <>
                  {t('leaves.fromYear')} {year} {t('leaves.yearShortOnlyBG')} {t('leaves.used')} {days}{' '}
                  {t('leaves.days')}
                  <br />
                </>
              ))}
            </div>
          ) : null;

        return tooltipContent ? (
          <TooltipWrapper
            id={`tooltip-date-from-${cell.row.id}`}
            overlay={tooltipContent}
          >
            <span>{formatDate(dateFrom, 'dd.mm.YYY') || ''}</span>
          </TooltipWrapper>
        ) : (
          <span>{formatDate(dateFrom, 'dd.mm.YYY') || ''}</span>
        );
      },
    },
    {
      Header: t('leaves.dateTo') || '',
      accessor: 'date_to',
      disableSortBy: true,
      width: '7.5rem',
      Cell: ({ cell }) => {
        const dateTo = new Date(cell.row.original.date_to);
        const subtractedDaysByYears = cell.row.original.subtracted_days_by_years;

        // Filter out entries where days are 0
        const filteredEntries = subtractedDaysByYears
          ? Object.entries(subtractedDaysByYears).filter(([_, days]) => days !== 0)
          : [];

        const tooltipContent =
          filteredEntries.length > 0 ? (
            <div>
              {filteredEntries.map(([year, days]) => (
                <>
                  {t('leaves.fromYear')} {year} {t('leaves.yearShortOnlyBG')} {t('leaves.used')} {days}{' '}
                  {t('leaves.days')}
                  <br />
                </>
              ))}
            </div>
          ) : null;

        return tooltipContent ? (
          <TooltipWrapper
            id={`tooltip-date-to-${cell.row.id}`}
            overlay={tooltipContent}
          >
            <span>{formatDate(dateTo, 'dd.mm.YYY') || ''}</span>
          </TooltipWrapper>
        ) : (
          <span>{formatDate(dateTo, 'dd.mm.YYY') || ''}</span>
        );
      },
    },
    {
      Header: t('leaves.numberOfDays') || '',
      accessor: 'leave_days',
      disableSortBy: true,
      width: '6rem',
      Cell: ({ cell }) => {
        const subtractedDaysByYears = cell.row.original.subtracted_days_by_years;
        const leaveDays = cell.row.original.leave_days;

        // Filter out entries where days are 0
        const filteredEntries = subtractedDaysByYears
          ? Object.entries(subtractedDaysByYears).filter(([_, days]) => days !== 0)
          : [];

        const tooltipContent =
          filteredEntries.length > 0 ? (
            <div>
              {filteredEntries.map(([year, days]) => (
                <>
                  {t('leaves.fromYear')} {year} {t('leaves.yearShortOnlyBG')} {t('leaves.used')} {days}{' '}
                  {t('leaves.days')}
                  <br />
                </>
              ))}
            </div>
          ) : null;

        return tooltipContent ? (
          <TooltipWrapper
            id={`tooltip-leave-days-${cell.row.id}`}
            overlay={tooltipContent}
          >
            <span>{isIntigerOrFloat(leaveDays) || ''}</span>
          </TooltipWrapper>
        ) : (
          <span>{isIntigerOrFloat(leaveDays) || ''}</span>
        );
      },
    },
    {
      Header: t('leaves.status') || '',
      accessor: 'status',
      disableSortBy: true,
      width: '7.5rem',
      Cell: ({ row }) => {
        return (
          <TooltipWrapper
            id={`tooltip-status-${row.original.id}`}
            overlay={getTooltipContent(row.original)}
          >
            <span>{renderBadge(row.original.status)}</span>
          </TooltipWrapper>
        );
      },
    },
    {
      Header: t('leaves.insertType') || '',
      accessor: 'insert_type',
      disableSortBy: true,
      width: '7.5rem',
      Cell: ({ cell }) => {
        return <>{cell.row.original.is_manually_added ? t('leaves.insertTypeManually') : ''}</>;
      },
    },
    {
      Header: () => <div style={{ textAlign: 'right' }}>{t('table.actions')}</div>,
      accessor: 'actions',
      disableSortBy: true,
      width: '20%',
      Cell: ({ row }) => {
        const {
          buttons,
          id,
          request_document_id: requestDocId,
          reject_request_document_id: employeeRejectionRequestId,
          order_document_id: orderDocId,
          reject_document_id: rejectDocId,
          status,
          attachments,
          comment: commentText,
          type,
        } = row.original;

        return (
          <div
            className="d-flex flex-wrap justify-content-end "
            style={{ textAlign: 'right', gap: '.5rem 0' }}
          >
            {renderActions(
              buttons,
              id,
              requestDocId,
              employeeRejectionRequestId,
              orderDocId,
              rejectDocId,
              attachments,
              status,
              commentText,
              type === 'sick',
              row.original
            )}
          </div>
        );
      },
    },
  ];

  const {
    isFetching,
    isLoading: isLoadingLeavesDropdowns,
    error: errorDropdowns,
    data: dropdowns,
  } = useQuery<{ [key: string]: ISelectOptionProps[] }, Error>(
    [buildClientEntity('leaves-dropdowns', client)],
    (): Promise<{ [key: string]: ISelectOptionProps[] }> => {
      return API.get('leaves/dropdown-options').then(res => {
        const filters = res.data && res.data[language || 'bg'];
        return {
          ...filters,
          status: filters.status.map((filter: ITableFilter) => {
            if (
              filter.value === 'rejected' ||
              filter.value === 'confirmed' ||
              filter.value === 'employeeRejectInitiated' ||
              filter.value === 'interrupted'
            )
              return {
                ...filter,
                selected: true,
              };

            return filter;
          }),
        };
      });
    },
    {
      cacheTime: 0,
    }
  );

  const tableFilters = useMemo(() => {
    const filters = [
      buildFilters({
        name: 'leave_period_start',
        placeholder: t('leaves.dateFrom'),
        ariaLabel: 'Leave date start',
        type: 'monthyearpickerstart',
      }),
      buildFilters({
        name: 'leave_period_end',
        placeholder: t('leaves.dateTo'),
        ariaLabel: 'Leave date end',
        type: 'monthyearpickerend',
        subType: 'leavesCompleted',
        setLastMonth: true,
      }),
      buildFilters({
        name: 'status',
        placeholder: t('table.statusFilter'),
        options: dropdowns?.status,
        isMulti: true,
        showTotal: true,
        allowSelectAll: true,
      }),
      buildFilters({
        name: 'user',
        placeholder: t('documents.table.employee'),
        options: dropdowns?.user,
        isMulti: true,
        limitOptions: true,
        showTotal: true,
        allowSelectAll: true,
        size: 'large',
      }),
      buildFilters({
        name: 'group',
        placeholder: t('table.groupFilter'),
        options: dropdowns?.group,
        isMulti: true,
        showTotal: true,
        allowSelectAll: true,
        size: 'large',
      }),
      buildFilters({
        name: 'type',
        ariaLabel: 'Leave type',
        placeholder: t('table.typeFilter'),
        options: dropdowns?.type,
        isMulti: true,
        showTotal: true,
        allowSelectAll: true,
      }),
    ];

    if (featureLeaveReview) {
      filters.push(
        buildFilters({
          name: 'leave_review',
          placeholder: t('leaves.filterReview'),
          type: 'icon',
          icon: (
            <ReviewFilterIcon
              ariaLabel="Leave sick review filter"
              onToggle={setIsReviewActive}
            />
          ),
        })
      );
    }

    return filters;
  }, [dropdowns, t, featureLeaveReview]);

  return (
    <InnerWrapper
      canMultiSign
      downloadOnly
      customSelectableProps={customSelectableRowProps}
      setCustomSelectableRowProps={setCustomSelectableRowProps}
      setMultiActionActive={setMultiActionActive}
      leavesCompleteStatus={isReviewActive}
      isLoading={isFetching || isLoadingLeavesDropdowns}
    >
      <FetchTable<ILeave>
        loading={isFetching || isLoadingLeavesDropdowns}
        entityName={buildClientEntity(`leaves-${isReviewActive}`, client)}
        entityEndpoint="leaves/search"
        columns={columns}
        hasPermission={[PermissionKeys.CAN_MANAGE_MANUALLY_ADDED_LEAVES]}
        filters={tableFilters}
        pageLimit={10}
        pagination
        hideSearch
        onAdd={createGroup}
        addBtnLabel={t('leaves.manuallyInsertLeave')}
        showOnlyUnreviewd={isReviewActive}
        showLeaveReview
        disableMultiSort
        useBackendSorting
        isSelectable
        useCustomSelectBtn
        disablePagination={multiActionActive}
        customSelectableRowProps={customSelectableRowProps}
      />
    </InnerWrapper>
  );
};

export default Screen;
